import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Box, Typography, Avatar, TextField, InputAdornment,
  List, ListItem, Chip, Divider, IconButton, CircularProgress,
  Badge, Button, Tooltip, Skeleton, Collapse, Fab, Snackbar, Alert
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import SendIcon from "@mui/icons-material/Send";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ReplayIcon from "@mui/icons-material/Replay";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import InstagramIcon from "@mui/icons-material/Instagram";
import { Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, Select, FormControl, InputLabel } from "@mui/material";
import { fetchConversations, fetchConversationById, updateConversationStatus, sendAdminMessage, fetchMessagesBySession } from "../api/conversationApi";
import { getBotById } from "../api/botApi";
import { connectSocket, joinConversation, leaveConversation } from "../api/socket";
import DOMPurify from "dompurify";

const PANEL_BG = "#f4f6fb";
const WHITE = "#ffffff";
const BLUE = "#5b5ea6";
const LIGHT_BLUE = "#eef0ff";
const GREEN = "#2e7d32";
const TABS = ["All", "Live", "Active", "Closed"];
const ORANGE = "#e65100";

const CANNED_REPLIES = [
  "I'll look into this right away!",
  "Please hold on a moment.",
  "Can you provide more details?",
  "Thank you for your patience.",
  "Is there anything else I can help you with?",
];

function sanitizeHtmlForChats(input) {
  if (!input || typeof input !== 'string') return '';
  return DOMPurify.sanitize(input, {
    ALLOWED_TAGS: ['b', 'strong', 'i', 'em', 'u', 'br', 'p', 'span', 'div', 'ol', 'ul', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'a', 'code', 'pre', 'blockquote', 'img'],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'src', 'alt', 'class', 'style'],
    ADD_ATTR: ['target'],
  });
}

function playNotification() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(1100, ctx.currentTime + 0.08);
    gain.gain.setValueAtTime(0.18, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.35);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.35);
  } catch (e) { /* silent */ }
}

function exportConversation(convo, userName) {
  const name = userName || `Session_${convo.sessionId?.slice(-6)}`;
  const lines = [
    `Conversation Export`,
    `User: ${name}`,
    `Status: ${convo.status}`,
    `Date: ${new Date(convo.createdAt || convo.updatedAt).toLocaleString()}`,
    `Session: ${convo.sessionId}`,
    "─────────────────────────────",
    ...(convo?.messages || []).map(m => {
      const time = m.createdAt
        ? new Date(m.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
        : "";
      const sender = m.sender === "admin" ? "Admin" : m.sender === "user" ? name : "Bot";
      return `[${time}] ${sender}: ${m.text}`;
    }),
  ];
  const blob = new Blob([lines.join("\n")], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `chat_${name}_${new Date().toISOString().slice(0, 10)}.txt`;
  a.click();
  URL.revokeObjectURL(url);
}

function MessageSkeleton() {
  const rows = [
    { align: "flex-start", w: 220 },
    { align: "flex-end", w: 160 },
    { align: "flex-start", w: 280 },
    { align: "flex-end", w: 200 },
    { align: "flex-start", w: 180 },
    { align: "flex-end", w: 240 },
  ];
  return (
    <Box>
      {rows.map((r, i) => (
        <Box key={i} sx={{ display: "flex", justifyContent: r.align, mb: 2, gap: 1, alignItems: "flex-end" }}>
          {r.align === "flex-start" && <Skeleton variant="circular" width={30} height={30} />}
          <Skeleton variant="rounded" width={r.w} height={38} sx={{ borderRadius: r.align === "flex-end" ? "18px 18px 4px 18px" : "18px 18px 18px 4px" }} />
          {r.align === "flex-end" && <Skeleton variant="circular" width={30} height={30} />}
        </Box>
      ))}
    </Box>
  );
}

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function getInitial(name) {
  return (name || "?")[0].toUpperCase();
}

const avatarColors = ["#5b5ea6", "#f57c00", "#2e7d32", "#c62828", "#00838f", "#6a1b9a"];
function avatarColor(str) {
  str = str || "";
  let h = 0;
  for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h << 5) - h);
  return avatarColors[Math.abs(h) % avatarColors.length];
}

function getLastMsgPreview(c) {
  const msg = c.messages?.[c.messages.length - 1];
  if (!msg) return "No messages";
  const prefix = msg.sender === "admin" ? "You: " : msg.sender === "bot" ? "Bot: " : "";
  return prefix + (msg.text?.slice(0, 38) || "");
}

export default function Chats() {
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [convo, setConvo] = useState(null);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("All");
  const [loadingConvo, setLoadingConvo] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingList, setLoadingList] = useState(true);
  const [adminText, setAdminText] = useState("");
  const [sending, setSending] = useState(false);
  const [showCanned, setShowCanned] = useState(false);
  const [showScrollBtn, setShowScrollBtn] = useState(false);
  const [dark, setDark] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });
  const [transferOpen, setTransferOpen] = useState(false);
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState("");
  const [transferring, setTransferring] = useState(false);
  const [botFlow, setBotFlow] = useState([]);

  const API = process.env.REACT_APP_AUTH_API
    ? (process.env.REACT_APP_AUTH_API).replace("/api/auth", "/api")
    : "/api";
  const messagesEndRef = useRef(null);
  const livePollingRef = useRef(null);
  const listRefreshRef = useRef(null);
  const activeIdRef = useRef(null);
  const prevMsgCountRef = useRef(0);
  const scrollBoxRef = useRef(null);
  const socketRef = useRef(null);

  const loadConversations = useCallback(async (silent = false) => {
    if (!silent) setRefreshing(true);
    try {
      const botId = localStorage.getItem('selectedBotId') || '';
      const data = await fetchConversations(1, 50, botId);
      setConversations(data);
    } catch (err) {
      console.error(err);
    } finally {
      setRefreshing(false);
      setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  useEffect(() => {
    const total = conversations.reduce((sum, c) => sum + (c.unreadCount || 0), 0);
    window.dispatchEvent(new CustomEvent('unreadUpdate', { detail: total }));
  }, [conversations]);

  // Restore selected chat on page load/refresh
  useEffect(() => {
    if (!loadingList && conversations.length > 0) {
      const savedId = sessionStorage.getItem('selectedChatId');
      if (savedId && !activeId && conversations.find(c => c._id === savedId)) {
        handleSelect(savedId);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingList]);

  // Re-fetch when selectedBotId changes or tab gets focus
  useEffect(() => {
    let lastBotId = localStorage.getItem('selectedBotId');
    const checkBotId = () => {
      const current = localStorage.getItem('selectedBotId');
      if (current !== lastBotId) { lastBotId = current; loadConversations(true); }
    };
    window.addEventListener('focus', checkBotId);
    window.addEventListener('visibilitychange', () => { if (!document.hidden) checkBotId(); });
    return () => {
      window.removeEventListener('focus', checkBotId);
      window.removeEventListener('visibilitychange', checkBotId);
    };
  }, [loadConversations]);

  // Auto-refresh list every 30s to catch new live requests
  useEffect(() => {
    listRefreshRef.current = setInterval(() => loadConversations(true), 30000);
    return () => clearInterval(listRefreshRef.current);
  }, [loadConversations]);

  useEffect(() => {
    activeIdRef.current = activeId;
  }, [activeId]);

  // Poll messages for live conversations (fallback when WebSocket unavailable)
  useEffect(() => {
    if (livePollingRef.current) clearInterval(livePollingRef.current);
    if (!convo || convo.status !== "live_requested") return;
    prevMsgCountRef.current = convo.messages?.length || 0;

    livePollingRef.current = setInterval(async () => {
      if (!convo?.chatbotId || !convo?.sessionId) return;
      try {
        const res = await fetchMessagesBySession(convo.chatbotId, convo.sessionId);
        if (res.ok) {
          const newLen = res.messages.length;
          if (newLen > prevMsgCountRef.current) {
            const newMsgs = res.messages.slice(prevMsgCountRef.current);
            if (newMsgs.some(m => m.sender === "user")) {
              playNotification();
            }
            prevMsgCountRef.current = newLen;
          }
          setConvo(prev => prev ? { ...prev, messages: res.messages, status: res.status } : prev);
        }
      } catch { /* silent */ }
    }, 4000);

    return () => clearInterval(livePollingRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convo?.status, convo?._id]);

  // Connect socket and listen for live updates (supplement to polling)
  useEffect(() => {
    const socket = connectSocket();
    socketRef.current = socket;

    if (socket?.connected && convo?.chatbotId && convo?.sessionId) {
      joinConversation(convo.chatbotId, convo.sessionId);
    }
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const userId = user?.user?._id || user?._id;
    if (socket?.connected && userId) {
      socket.emit("join-agent", { agentId: userId, ownerId: userId });
    }

    if (!socket?.connected) return;

    const handleMessageUpdate = async ({ chatbotId, sessionId }) => {
      const isCurrent = convo?.chatbotId === chatbotId && convo?.sessionId === sessionId;
      setConversations(prev => prev.map(c =>
        c.chatbotId === chatbotId && c.sessionId === sessionId
          ? { ...c, updatedAt: new Date().toISOString(), unreadCount: isCurrent ? 0 : (c.unreadCount || 0) + 1 }
          : c
      ));
      if (isCurrent) {
        playNotification();
        try {
          const res = await fetchMessagesBySession(chatbotId, sessionId);
          if (res.ok) {
            prevMsgCountRef.current = res.messages.length;
            setConvo(prev => prev ? { ...prev, messages: res.messages, status: res.status } : prev);
          }
        } catch { /* silent */ }
      } else {
        loadConversations(true);
      }
    };

    const handleStatusUpdate = ({ chatbotId, sessionId, status }) => {
      if (convo?.chatbotId === chatbotId && convo?.sessionId === sessionId) {
        setConvo(prev => prev ? { ...prev, status } : prev);
      }
      setConversations(prev =>
        prev.map(c => c.chatbotId === chatbotId && c.sessionId === sessionId ? { ...c, status } : c)
      );
    };

    const handleLiveRequest = () => loadConversations(true);

    socket.on("message-update", handleMessageUpdate);
    socket.on("status-updated", handleStatusUpdate);
    socket.on("live-request", handleLiveRequest);

    return () => {
      if (convo?.chatbotId && convo?.sessionId) {
        leaveConversation(convo.chatbotId, convo.sessionId);
      }
      socket.off("message-update", handleMessageUpdate);
      socket.off("status-updated", handleStatusUpdate);
      socket.off("live-request", handleLiveRequest);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convo?.chatbotId, convo?.sessionId, convo?._id]);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [convo?.messages?.length]);

  // Fetch bot flow when conversation changes — needed for per-type answer rendering
  useEffect(() => {
    const botId = convo?.botId;
    if (!botId) { setBotFlow([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const bot = await getBotById(botId);
        if (!cancelled) setBotFlow(bot?.flowSetupSetting?.question?.list || []);
      } catch (err) {
        if (!cancelled) setBotFlow([]);
      }
    })();
    return () => { cancelled = true; };
  }, [convo?.botId]);

  const questionMap = React.useMemo(() => {
    const m = {};
    (botFlow || []).forEach((q) => { if (q?.id) m[q.id] = q; });
    return m;
  }, [botFlow]);

  // Rich accent for a user answer based on the question type.
  // Returns a small chip/icon/grid rendered ABOVE the plain text inside the message bubble.
  const renderAnswerAccent = (m) => {
    if (!m?.questionId) return null;
    const q = questionMap[m.questionId];
    if (!q) return null;
    const type = q.type;
    const text = m.text || "";
    const wrap = (icon, label, body) => (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, mb: 0.6, pb: 0.5, borderBottom: "1px solid rgba(255,255,255,0.18)" }}>
        <Box sx={{ fontSize: 12 }}>{icon}</Box>
        <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.3, textTransform: "uppercase", opacity: 0.85 }}>{label}</Typography>
        {body}
      </Box>
    );
    if (type === "form" || type === "ask_contacts") {
      const lines = text.split("\n").map((l) => l.trim()).filter(Boolean);
      if (lines.length === 0) return null;
      return (
        <Box sx={{ mb: 0.6, pb: 0.6, borderBottom: "1px solid rgba(255,255,255,0.18)" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, mb: 0.4 }}>
            <Box sx={{ fontSize: 12 }}>{type === "form" ? "📝" : "👤"}</Box>
            <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.3, textTransform: "uppercase", opacity: 0.85 }}>{type === "form" ? "Form" : "Contact"}</Typography>
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: "auto 1fr", columnGap: 1, rowGap: 0.3 }}>
            {lines.map((line, i) => {
              const idx = line.indexOf(":");
              if (idx === -1) return <React.Fragment key={i}><Box /><Typography sx={{ fontSize: 11, opacity: 0.95 }}>{line}</Typography></React.Fragment>;
              const k = line.slice(0, idx).trim();
              const v = line.slice(idx + 1).trim();
              return (
                <React.Fragment key={i}>
                  <Typography sx={{ fontSize: 10.5, opacity: 0.7, fontWeight: 600 }}>{k}</Typography>
                  <Typography sx={{ fontSize: 11.5, fontWeight: 600, wordBreak: "break-word" }}>{v || "—"}</Typography>
                </React.Fragment>
              );
            })}
          </Box>
        </Box>
      );
    }
    if (type === "appointment_booking") {
      const m1 = text.match(/(\d{4}-\d{2}-\d{2})/);
      const m2 = text.match(/(\d{2}:\d{2})/);
      if (!m1 && !m2) return null;
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, mb: 0.6, pb: 0.5, borderBottom: "1px solid rgba(255,255,255,0.18)" }}>
          <Box sx={{ fontSize: 12 }}>📅</Box>
          <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.3, textTransform: "uppercase", opacity: 0.85 }}>Appointment</Typography>
          <Typography sx={{ fontSize: 11, fontWeight: 700, ml: "auto" }}>{m1 ? m1[1] : ""}{m2 ? ` · ${m2[1]}` : ""}</Typography>
        </Box>
      );
    }
    if (type === "authenticator") {
      const codeMatch = text.match(/\[Verified OTP\]\s*(\S+)/) || text.match(/\b([A-Z0-9]{4,8})\b/);
      const code = codeMatch ? codeMatch[1] : null;
      if (!code) return null;
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, mb: 0.6, pb: 0.5, borderBottom: "1px solid rgba(255,255,255,0.18)" }}>
          <Box sx={{ fontSize: 12 }}>🔐</Box>
          <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.3, textTransform: "uppercase", opacity: 0.85 }}>OTP</Typography>
          <Typography sx={{ fontSize: 13, fontWeight: 800, letterSpacing: 2, ml: "auto", fontFamily: "ui-monospace, monospace" }}>{code}</Typography>
        </Box>
      );
    }
    if (type === "rating") {
      const m1 = text.match(/\((\d+)\/5\)/);
      const stars = m1 ? parseInt(m1[1], 10) : 0;
      if (!stars) return null;
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, mb: 0.6, pb: 0.5, borderBottom: "1px solid rgba(255,255,255,0.18)" }}>
          <Typography sx={{ fontSize: 12 }}>{"★".repeat(stars)}<span style={{ opacity: 0.3 }}>{"★".repeat(5 - stars)}</span></Typography>
          <Typography sx={{ fontSize: 10, fontWeight: 700, opacity: 0.85, ml: 0.5 }}>{stars}/5</Typography>
        </Box>
      );
    }
    if (type === "date_picker") {
      return wrap("📅", "Date", <Typography sx={{ fontSize: 11, fontWeight: 700, ml: "auto" }}>{text}</Typography>);
    }
    if (type === "time_picker") {
      return wrap("⏰", "Time", <Typography sx={{ fontSize: 11, fontWeight: 700, ml: "auto" }}>{text}</Typography>);
    }
    if (type === "range") {
      return wrap("🎚️", "Range", <Typography sx={{ fontSize: 11, fontWeight: 700, ml: "auto" }}>{text}</Typography>);
    }
    if (type === "mobile_number" || type === "mobile") {
      return wrap("📱", "Phone", <Typography sx={{ fontSize: 11, fontWeight: 700, ml: "auto" }}>{text}</Typography>);
    }
    if (type === "email" || type === "email_feild") {
      return wrap("✉️", "Email", <Typography sx={{ fontSize: 11, fontWeight: 700, ml: "auto", wordBreak: "break-all" }}>{text}</Typography>);
    }
    if (type === "file_upload") {
      return wrap("📎", "File", <Typography sx={{ fontSize: 11, fontWeight: 700, ml: "auto" }}>{text.replace(/^📎\s*/, "").split(/\s+/)[0]}</Typography>);
    }
    if (type === "carousel_with_buttons" || type === "order_items") {
      const icon = type === "carousel_with_buttons" ? "🎠" : "🛒";
      const label = type === "carousel_with_buttons" ? "Carousel" : "Order";
      const opt = (q.options || []).find((o) => (typeof o === "object" ? o.title || o.name : String(o)) === text);
      const desc = opt && typeof opt === "object" ? (opt.description || opt.price ? `₹${opt.price}` : "") : "";
      return wrap(icon, label, <Typography sx={{ fontSize: 11, fontWeight: 700, ml: "auto" }}>{text}{desc ? ` · ${desc}` : ""}</Typography>);
    }
    if (type === "Real_time_search") {
      return wrap("🔍", "Search", <Typography sx={{ fontSize: 11, fontWeight: 700, ml: "auto" }}>{text}</Typography>);
    }
    if (type === "multiple_options" || type === "single_choice") {
      return wrap("☑️", "Choice", <Typography sx={{ fontSize: 11, fontWeight: 700, ml: "auto" }}>{text}</Typography>);
    }
    if (type === "dynamic_question") {
      return wrap("⚡", "API", <Typography sx={{ fontSize: 11, fontWeight: 700, ml: "auto" }}>{text}</Typography>);
    }
    return null;
  };

  const handleScroll = useCallback(() => {
    const box = scrollBoxRef.current;
    if (!box) return;
    setShowScrollBtn(box.scrollHeight - box.scrollTop - box.clientHeight > 120);
  }, []);

  const handleSelect = async (id) => {
    setActiveId(id);
    sessionStorage.setItem('selectedChatId', id);
    setAdminText("");
    setShowCanned(false);
    setLoadingConvo(true);
    if (livePollingRef.current) clearInterval(livePollingRef.current);
    setConversations(prev =>
      prev.map(c => c._id === id ? { ...c, unreadCount: 0 } : c)
    );
    try {
      const data = await fetchConversationById(id);
      setConvo(data);
      prevMsgCountRef.current = data.messages?.length || 0;
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingConvo(false);
    }
  };

  const handleAdminSend = async () => {
    if (!adminText.trim() || !convo || sending) return;
    const text = adminText.trim();
    setAdminText("");
    setShowCanned(false);
    setSending(true);
    const optimistic = { sender: "admin", text, createdAt: new Date().toISOString() };
    setConvo(prev => prev ? { ...prev, messages: [...prev.messages, optimistic] } : prev);
    prevMsgCountRef.current += 1;
    try {
      await sendAdminMessage(convo.chatbotId, convo.sessionId, text);
    } catch (err) {
      console.error(err);
    } finally {
      setSending(false);
    }
  };

  const handleStatusToggle = async () => {
    if (!activeId || !convo) return;
    const newStatus = convo.status !== "closed" ? "closed" : "active";
    setStatusUpdating(true);
    try {
      await updateConversationStatus(activeId, newStatus);
      setConvo(prev => ({ ...prev, status: newStatus }));
      setConversations(prev =>
        prev.map(c => c._id === activeId ? { ...c, status: newStatus } : c)
      );
    } catch (err) {
      console.error(err);
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleTransferOpen = async () => {
    try {
      const res = await fetch(`/api/agents`, { credentials: "include" });
      const data = await res.json();
      setAgents(Array.isArray(data) ? data : []);
    } catch {
      setAgents([]);
    } finally {
      setSelectedAgent("");
      setTransferOpen(true);
    }
  };

  const handleTransfer = async () => {
    if (!selectedAgent || !convo) return;
    setTransferring(true);
    try {
      await fetch(`/api/live-chat/transfer`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ chatbotId: convo.chatbotId, sessionId: convo.sessionId, toAgentId: selectedAgent }),
      });
      const agentObj = agents.find(a => a._id === selectedAgent);
      if (agentObj) setConvo(prev => ({ ...prev, agentName: agentObj.fullName, agentStatus: agentObj.agentStatus }));
      setTransferOpen(false);
      // Refresh convo to get latest data
      if (activeId) {
        const fresh = await fetchConversationById(activeId);
        if (fresh) setConvo(fresh);
      }
      const isOffline = agentObj?.agentStatus !== "online";
      setSnack({
        open: true,
        message: isOffline
          ? `Chat transferred to ${agentObj?.fullName} (offline — will receive when online)`
          : `Chat successfully transferred to ${agentObj?.fullName}`,
        severity: isOffline ? "warning" : "success"
      });
    } catch {}
    finally { setTransferring(false); }
  };

  const activeCount = conversations.filter(c => c.status === "active").length;  const closedCount = conversations.filter(c => c.status === "closed").length;
  const liveCount = conversations.filter(c => c.status === "live_requested").length;

  const filtered = conversations
    .filter(c => {
      if (tab === "Active") return c.status === "active";
      if (tab === "Closed") return c.status === "closed";
      if (tab === "Live") return c.status === "live_requested";
      return true;
    })
    .filter(c =>
      (c.userName || c.sessionId || "").toLowerCase().includes(search.toLowerCase())
    );

  const activeConvo = conversations.find(c => c._id === activeId);
  const userMessages = convo?.messages?.filter(m => m.sender === "user").length || 0;
  const botMessages = convo?.messages?.filter(m => m.sender === "bot").length || 0;
  const isLive = convo?.status === "live_requested";

  const T = {
    bg:       dark ? "#0f172a" : "#ffffff",
    sidebar:  dark ? "#1e293b" : "#ffffff",
    panel:    dark ? "#0f172a" : "#f4f6fb",
    border:   dark ? "#334155" : "#eeeeee",
    text:     dark ? "#f1f5f9" : "#111827",
    subtext:  dark ? "#94a3b8" : "#6b7280",
    msgBg:    dark ? "#1e293b" : "#ffffff",
    input:    dark ? "#1e293b" : "#f4f6fb",
  };

  return (
    <Box sx={{ display: "flex", height: "calc(100vh - 100px)", gap: 0, borderRadius: 3, overflow: "hidden", boxShadow: "0 4px 24px rgba(0,0,0,0.08)" }}>

      {/* ─── LEFT PANEL ─── */}
      <Box sx={{ width: 300, minWidth: 300, bgcolor: T.sidebar, display: "flex", flexDirection: "column", borderRight: `1px solid ${T.border}` }}>
        <Box sx={{ p: 2.5, pb: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <ChatBubbleOutlineIcon sx={{ color: BLUE, fontSize: 20 }} />
              <Typography fontWeight={700} fontSize={16} color={T.text}>Chats</Typography>
              {activeCount > 0 && (
                <Chip label={activeCount} size="small" sx={{ bgcolor: "#e8f5e9", color: GREEN, fontWeight: 700, height: 20, fontSize: 11 }} />
              )}
            </Box>
            <Tooltip title="Refresh">
              <IconButton size="small" onClick={() => loadConversations()} disabled={refreshing}
                sx={{ '&:hover svg': { transform: 'rotate(-360deg)' }, '& svg': { transition: 'transform 0.5s ease' } }}>
                <RefreshIcon fontSize="small" sx={{ color: BLUE, animation: refreshing ? "spin 1s linear infinite" : "none" }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Export CSV">
              <IconButton size="small" onClick={() => {
                const base = process.env.REACT_APP_AUTH_API ? process.env.REACT_APP_AUTH_API.replace("/auth", "") : "http://localhost:5000/api";
                fetch(`${base}/conversation/export-csv`, { credentials: "include" })
                  .then(r => r.blob()).then(blob => { const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "chats-export.csv"; a.click(); });
              }}>
                <DownloadIcon fontSize="small" sx={{ color: "#10b981" }} />
              </IconButton>
            </Tooltip>
            <Tooltip title={dark ? "Light mode" : "Dark mode"}>
              <IconButton size="small" onClick={() => setDark(d => !d)}>
                {dark ? <LightModeIcon fontSize="small" sx={{ color: "#f59e0b" }} /> : <DarkModeIcon fontSize="small" sx={{ color: "#6366f1" }} />}
              </IconButton>
            </Tooltip>
          </Box>

          {/* Tabs */}
          <Box sx={{ display: "flex", gap: 0.5, mb: 2, bgcolor: PANEL_BG, borderRadius: 2, p: 0.5 }}>
            {TABS.map((t) => {
              const count = t === "Active" ? activeCount : t === "Closed" ? closedCount : t === "Live" ? liveCount : conversations.length;
              return (
                <Box
                  key={t}
                  onClick={() => setTab(t)}
                  sx={{
                    flex: 1, textAlign: "center",
                    px: 1, py: 0.6, borderRadius: 1.5, fontSize: 12, fontWeight: 600,
                    cursor: "pointer",
                    bgcolor: tab === t ? WHITE : t === "Live" && liveCount > 0 ? "#fff3e0" : "transparent",
                    color: tab === t ? (t === "Live" ? ORANGE : BLUE) : t === "Live" && liveCount > 0 ? ORANGE : "#888",
                    boxShadow: tab === t ? "0 1px 4px rgba(0,0,0,0.1)" : "none",
                    animation: t === "Live" && liveCount > 0 && tab !== "Live" ? "pulse 1.5s ease infinite" : "none",
                    "@keyframes pulse": { "0%,100%": { opacity: 1 }, "50%": { opacity: 0.55 } },
                    transition: "all 0.15s",
                  }}
                >
                  {t}
                  {count > 0 && (
                    <Box component="span" sx={{ ml: 0.5, fontSize: 10, opacity: 0.7, color: t === "Live" ? ORANGE : "inherit" }}>({count})</Box>
                  )}
                </Box>
              );
            })}
          </Box>

          {/* Search */}
          <TextField
            size="small"
            fullWidth
            placeholder="Search chats..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: "#aaa" }} /></InputAdornment>,
              sx: { borderRadius: 3, bgcolor: PANEL_BG, fontSize: 13 }
            }}
            sx={{ "& fieldset": { border: "none" } }}
          />
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ px: 2.5, mb: 1, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
          {loadingList ? <Skeleton variant="text" width={80} /> : `${tab} · ${filtered.length}`}
        </Typography>

        {/* User List */}
        <List disablePadding sx={{ overflowY: "auto", flex: 1, px: 1 }}>
          {loadingList ? (
            [...Array(6)].map((_, i) => (
              <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 1.5, py: 1.2, mb: 0.5 }}>
                <Skeleton variant="circular" width={42} height={42} sx={{ flexShrink: 0 }} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton variant="text" width="60%" height={16} sx={{ mb: 0.5 }} />
                  <Skeleton variant="text" width="85%" height={13} />
                </Box>
                <Skeleton variant="text" width={28} height={12} />
              </Box>
            ))
          ) : filtered.length === 0 ? (
            <Box sx={{ p: 3, textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                {tab === "Active" ? "No active chats." : tab === "Closed" ? "No closed chats." : tab === "Live" ? "No live requests." : "No conversations yet."}
              </Typography>
            </Box>
          ) : (
            filtered.map((c) => {
              const name = c.userName || `Session ${c.sessionId?.slice(-6)}`;
              const preview = getLastMsgPreview(c);
              const isSelected = c._id === activeId;
              const isItemLoading = isSelected && loadingConvo;
              const isClosed = c.status === "closed";
              const isItemLive = c.status === "live_requested";
              return (
                <ListItem
                  key={c._id}
                  button
                  onClick={() => handleSelect(c._id)}
                  sx={{
                    borderRadius: 2.5, mb: 0.5, px: 1.5, py: 1.2,
                    bgcolor: isItemLive ? "#fff3e0" : isSelected ? LIGHT_BLUE : "transparent",
                    borderLeft: isItemLive ? `3px solid ${ORANGE}` : "3px solid transparent",
                    "&:hover": { bgcolor: isItemLive ? "#ffe0b2" : isSelected ? LIGHT_BLUE : PANEL_BG },
                    transition: "background 0.15s",
                    opacity: isClosed ? 0.65 : 1,
                  }}
                >
                  <Box sx={{ position: "relative", mr: 1.5, flexShrink: 0 }}>
                    <Badge
                      variant="dot"
                      color={isItemLive ? "warning" : "success"}
                      invisible={isClosed || isItemLoading}
                      overlap="circular"
                      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    >
                      <Avatar sx={{ width: 42, height: 42, bgcolor: avatarColor(name), fontSize: 16, fontWeight: 700, opacity: isItemLoading ? 0.5 : 1, transition: "opacity 0.2s" }}>
                        {getInitial(name)}
                      </Avatar>
                    </Badge>
                    {isItemLoading && (
                      <CircularProgress size={46} thickness={2.5} sx={{ color: BLUE, position: "absolute", top: -2, left: -2, zIndex: 1 }} />
                    )}
                  </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Typography fontSize={13.5} fontWeight={isSelected ? 700 : 600} noWrap sx={{ maxWidth: 120, color: T.text }}>
                        {name}
                      </Typography>
                      {c.chatbotId?.startsWith("instagram_") && (
                        <InstagramIcon sx={{ fontSize: 14, color: "#e1306c", ml: 0.5 }} />
                      )}
                      <Typography fontSize={11} color={T.subtext}>{timeAgo(c.updatedAt)}</Typography>
                      {(c.unreadCount || 0) > 0 && !isSelected && (
                        <Box sx={{
                          bgcolor: '#5b5ea6', color: '#fff', borderRadius: '50%',
                          minWidth: 18, height: 18, display: 'flex', alignItems: 'center',
                          justifyContent: 'center', fontSize: 10, fontWeight: 700, ml: 0.5,
                          px: c.unreadCount > 9 ? 0.6 : 0,
                        }}>
                          {c.unreadCount > 99 ? '99+' : c.unreadCount}
                        </Box>
                      )}
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
                      {isClosed && <Chip label="closed" size="small" sx={{ height: 14, fontSize: 9, px: 0.3, color: "#888", bgcolor: "#f0f0f0" }} />}
                      {isItemLive && <Chip label="🔴 Live" size="small" sx={{ height: 14, fontSize: 9, px: 0.3, color: ORANGE, bgcolor: "#fff3e0", fontWeight: 700 }} />}
                      {c.intent && (
                        <Chip
                          label={
                            c.intent === 'book' ? '📅 Book' :
                            c.intent === 'shop' ? '🛍️ Shop' :
                            c.intent === 'help' ? '💬 Help' :
                            `🏷️ ${c.intent}`
                          }
                          size="small"
                          sx={{ height: 16, fontSize: 9, fontWeight: 700, bgcolor: '#ede9fe', color: '#5b21b6' }}
                        />
                      )}
                      {c.botName && (
                        <Chip
                          icon={<SmartToyIcon sx={{ fontSize: 10 }} />}
                          label={c.botName}
                          size="small"
                          sx={{ height: 16, fontSize: 9, "& .MuiChip-icon": { ml: 0.3, fontSize: 10 } }}
                        />
                      )}
                      {c.rating && <Chip label={`⭐ ${c.rating}/5`} size="small" sx={{ height: 16, fontSize: 9, bgcolor: '#fef3c7', color: '#92400e', fontWeight: 700 }} />}
                      <Typography fontSize={12} color={T.subtext} noWrap sx={{ maxWidth: 150 }}>
                        {preview}
                      </Typography>
                    </Box>
                  </Box>
                </ListItem>
              );
            })
          )}
        </List>
      </Box>

      {/* ─── CENTER PANEL ─── */}
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", bgcolor: T.panel }}>
        {!activeId ? (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", gap: 2 }}>
            <ChatBubbleOutlineIcon sx={{ fontSize: 56, color: "#ccc" }} />
            <Typography color="text.secondary" fontWeight={500}>Select a conversation to view messages</Typography>
          </Box>
        ) : (
          <>
            {/* Chat Header */}
            <Box sx={{ bgcolor: T.bg, px: 3, py: 2, display: "flex", alignItems: "center", gap: 2, borderBottom: `2px solid ${isLive ? ORANGE : T.border}` }}>
              <Avatar sx={{ bgcolor: avatarColor(activeConvo?.userName || ""), fontWeight: 700 }}>
                {getInitial(activeConvo?.userName)}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography fontWeight={700} fontSize={15} color={T.text}>
                  {activeConvo?.userName || `Session ${activeConvo?.sessionId?.slice(-6)}`}
                </Typography>
                <Typography variant="caption" color={isLive ? ORANGE : T.subtext} fontWeight={isLive ? 700 : 400}>
                  {isLive ? "🔴 Live chat in progress" : `Last active ${timeAgo(activeConvo?.updatedAt)}`}
                </Typography>
              </Box>
              {loadingConvo ? (
                <Skeleton variant="rounded" width={60} height={24} />
              ) : (
                <>
                  <Chip
                    label={isLive ? "Live" : convo?.status || "active"}
                    size="small"
                    sx={{
                      bgcolor: isLive ? "#fff3e0" : convo?.status === "active" ? "#e8f5e9" : "#f0f0f0",
                      color: isLive ? ORANGE : convo?.status === "active" ? GREEN : "#888",
                      fontWeight: 700,
                    }}
                  />
                  <Tooltip title={convo?.status !== "closed" ? "Close conversation" : "Reopen conversation"}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={convo?.status !== "closed" ? <CheckCircleIcon /> : <ReplayIcon />}
                      onClick={handleStatusToggle}
                      disabled={statusUpdating}
                      sx={{                        textTransform: "none", fontSize: 12,
                        borderColor: convo?.status !== "closed" ? "#c62828" : GREEN,
                        color: convo?.status !== "closed" ? "#c62828" : GREEN,
                        "&:hover": {
                          bgcolor: convo?.status !== "closed" ? "#ffebee" : "#e8f5e9",
                          borderColor: convo?.status !== "closed" ? "#c62828" : GREEN,
                        }
                      }}
                    >
                      {statusUpdating ? "..." : convo?.status !== "closed" ? "Close" : "Reopen"}
                    </Button>
                  </Tooltip>
                  {(isLive || convo?.status === "live_active") && (
                    <Tooltip title="Transfer to agent">
                      <Button size="small" variant="outlined" startIcon={<SwapHorizIcon />}
                        onClick={handleTransferOpen}
                        sx={{ textTransform: "none", fontSize: 12, borderColor: "#6366f1", color: "#6366f1", "&:hover": { bgcolor: "#eef2ff" } }}>
                        Transfer
                      </Button>
                    </Tooltip>
                  )}
                </>
              )}
            </Box>

            {/* Messages Area */}
            <Box
              ref={scrollBoxRef}
              onScroll={handleScroll}
              sx={{ flex: 1, overflowY: "auto", px: 3, py: 2, position: "relative" }}
            >
              {loadingConvo ? (
                <MessageSkeleton />
              ) : !convo || !convo.messages || convo.messages.length === 0 ? (
                <Typography textAlign="center" color="text.secondary" mt={4}>No messages in this conversation.</Typography>
              ) : (
                <Box sx={{ animation: "fadeIn 0.25s ease", "@keyframes fadeIn": { from: { opacity: 0, transform: "translateY(6px)" }, to: { opacity: 1, transform: "translateY(0)" } } }}>
                  {convo.messages.map((m, i) => {
                    const isUser = m.sender === "user";
                    const isAdmin = m.sender === "admin" || m.sender === "agent";
                    const alignRight = isUser; // user right, bot/admin left
                    return (
                      <Box key={i} sx={{ display: "flex", flexDirection: "column", alignItems: alignRight ? "flex-end" : "flex-start", mb: 2 }}>
                        <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1, flexDirection: alignRight ? "row-reverse" : "row" }}>
                          <Avatar sx={{ width: 30, height: 30, fontSize: 13, bgcolor: isAdmin ? ORANGE : isUser ? avatarColor(activeConvo?.userName) : BLUE }}>
                            {isAdmin ? "S" : isUser ? getInitial(activeConvo?.userName) : "B"}
                          </Avatar>
                          <Box>
                            <Typography variant="caption" color={T.subtext} sx={{ mb: 0.5, display: "block", textAlign: alignRight ? "right" : "left" }}>
                              {isAdmin ? "Support" : isUser ? (activeConvo?.userName || "User") : "Bot"}
                              {m.createdAt && ` · ${new Date(m.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`}
                            </Typography>
                            <Box sx={{
                              px: 2, py: 1.2,
                              borderRadius: alignRight ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
                              bgcolor: isAdmin ? ORANGE : isUser ? BLUE : T.msgBg,
                              color: (isAdmin || isUser) ? "#fff" : T.text,
                              boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
                              maxWidth: 380,
                            }}>
                              <Typography fontSize={13.5} lineHeight={1.6} sx={{ wordBreak: 'break-word', '& p': { m: 0 }, '& ul, & ol': { m: 0, pl: 2.5 } }} component="div">
                                {isUser && renderAnswerAccent(m)}
                                {m.text?.startsWith('[IMG]') ? (
                                  <img src={m.text.replace('[IMG]', '')} alt="upload" style={{ maxWidth: '100%', maxHeight: 150, borderRadius: 8, display: 'block' }} />
                                ) : m.text?.startsWith('data:image/') ? (
                                  <img src={m.text} alt="upload" style={{ maxWidth: '100%', maxHeight: 150, borderRadius: 8, display: 'block' }} />
                                ) : /^📎\s.*\s(?:https?:\/\/|data:)\S+$/.test(m.text || '') ? (
                                  (() => {
                                    const t = m.text || '';
                                    const url = t.slice(t.lastIndexOf(' ') + 1);
                                    const name = t.replace(/^📎\s*/, '').replace(url, '').trim();
                                    return <a href={url} target="_blank" rel="noopener noreferrer" download={name} style={{ color: 'inherit', textDecoration: 'underline', wordBreak: 'break-all' }}>📎 {name}</a>;
                                  })()
                                ) : (m.sender === 'bot' || m.sender === 'admin' || m.sender === 'agent') ? (
                                  <span style={{ whiteSpace: 'pre-wrap' }} dangerouslySetInnerHTML={{ __html: sanitizeHtmlForChats(m.text || '') }} />
                                ) : (
                                  m.text
                                )}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    );
                  })}
                  {sending && (
                    <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1, mb: 2 }}>
                      <Avatar sx={{ width: 30, height: 30, fontSize: 13, bgcolor: ORANGE }}>S</Avatar>
                      <Box sx={{ px: 2, py: 1.2, borderRadius: "18px 18px 18px 4px", bgcolor: WHITE, boxShadow: "0 2px 8px rgba(0,0,0,0.07)", display: "flex", gap: 0.6, alignItems: "center" }}>
                        {[0, 1, 2].map(i => (
                          <Box key={i} sx={{
                            width: 7, height: 7, borderRadius: "50%", bgcolor: ORANGE,
                            animation: "typingBounce 1.4s infinite ease-in-out",
                            animationDelay: `${i * 0.2}s`,
                            "@keyframes typingBounce": {
                              "0%,80%,100%": { transform: "scale(0.6)", opacity: 0.4 },
                              "40%": { transform: "scale(1)", opacity: 1 },
                            },
                          }} />
                        ))}
                      </Box>
                    </Box>
                  )}
                  <div ref={messagesEndRef} />
                </Box>
              )}
            </Box>

            {/* Scroll to bottom FAB */}
            {showScrollBtn && (
              <Box sx={{ position: "absolute", bottom: isLive ? 140 : 90, left: "calc(300px + (100% - 560px) / 2)", zIndex: 10 }}>
                <Fab
                  size="small"
                  onClick={() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })}
                  sx={{ bgcolor: BLUE, color: "#fff", boxShadow: "0 3px 10px rgba(0,0,0,0.2)", "&:hover": { bgcolor: "#4a4d8a" } }}
                >
                  <KeyboardArrowDownIcon />
                </Fab>
              </Box>
            )}

            {/* Canned Replies */}
            {isLive && (
              <Collapse in={showCanned}>
                <Box sx={{ bgcolor: "#fff8f0", px: 2, py: 1.5, borderTop: "1px solid #ffe0b2", display: "flex", flexWrap: "wrap", gap: 0.8 }}>
                  {CANNED_REPLIES.map((r, i) => (
                    <Chip
                      key={i}
                      label={r}
                      size="small"
                      onClick={() => { setAdminText(r); setShowCanned(false); }}
                      sx={{ fontSize: 11.5, cursor: "pointer", bgcolor: WHITE, border: `1px solid #ffcc80`, "&:hover": { bgcolor: "#fff3e0" }, transition: "all 0.1s" }}
                    />
                  ))}
                </Box>
              </Collapse>
            )}

            {/* Input */}
            {isLive ? (
              <Box sx={{ bgcolor: T.bg, px: 2, py: 1.5, borderTop: `2px solid ${ORANGE}`, display: "flex", alignItems: "center", gap: 1 }}>
                <Tooltip title={showCanned ? "Hide quick replies" : "Quick replies"}>
                  <IconButton
                    size="small"
                    onClick={() => setShowCanned(p => !p)}
                    sx={{ color: showCanned ? ORANGE : "#bbb", "&:hover": { color: ORANGE }, transition: "color 0.15s" }}
                  >
                    <FlashOnIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Reply to user..."
                  value={adminText}
                  onChange={e => setAdminText(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && !e.shiftKey && handleAdminSend()}
                  disabled={sending}
                  autoFocus
                  InputProps={{ sx: { borderRadius: 3, fontSize: 13 } }}
                />
                <IconButton
                  onClick={handleAdminSend}
                  disabled={!adminText.trim() || sending}
                  sx={{ bgcolor: ORANGE, color: "#fff", "&:hover": { bgcolor: "#bf360c" }, "&.Mui-disabled": { bgcolor: "#eee" }, borderRadius: 2.5 }}
                >
                  {sending ? <CircularProgress size={18} color="inherit" /> : <SendIcon fontSize="small" />}
                </IconButton>
              </Box>
            ) : (
              <Box sx={{ bgcolor: T.bg, px: 3, py: 2, borderTop: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: 1.5 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder={convo?.status === "closed" ? "Conversation is closed." : "View-only — responses are automated by your flow."}
                  disabled
                  InputProps={{ sx: { borderRadius: 3, bgcolor: PANEL_BG, fontSize: 13 } }}
                  sx={{ "& fieldset": { border: "none" } }}
                />
                <IconButton disabled sx={{ bgcolor: "#eee", color: "#aaa", borderRadius: 2.5 }}>
                  <SendIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
          </>
        )}
      </Box>

      {/* ─── RIGHT PANEL ─── */}
      <Box sx={{ width: 260, minWidth: 260, bgcolor: T.sidebar, borderLeft: `1px solid ${T.border}`, p: 2.5, overflowY: "auto" }}>
        {loadingConvo && activeId ? (
          <Box sx={{ pt: 1 }}>
            <Skeleton variant="circular" width={60} height={60} sx={{ mx: "auto", mb: 1.5 }} />
            <Skeleton variant="text" width="60%" sx={{ mx: "auto", mb: 0.5 }} />
            <Skeleton variant="text" width="40%" sx={{ mx: "auto", mb: 2 }} />
            <Divider sx={{ mb: 2 }} />
            {[...Array(5)].map((_, i) => (
              <Box key={i} sx={{ display: "flex", justifyContent: "space-between", py: 1 }}>
                <Skeleton variant="text" width="45%" />
                <Skeleton variant="text" width="30%" />
              </Box>
            ))}
          </Box>
        ) : convo ? (
          <>
            {/* User Info */}
            <Box sx={{ textAlign: "center", mb: 3 }}>
              <Avatar sx={{ width: 60, height: 60, mx: "auto", mb: 1.5, bgcolor: avatarColor(activeConvo?.userName || ""), fontSize: 24, fontWeight: 700 }}>
                {getInitial(activeConvo?.userName)}
              </Avatar>
              <Typography fontWeight={700} fontSize={15} color={T.text}>
                {activeConvo?.userName || "Anonymous"}
              </Typography>
              <Typography variant="caption" color={T.subtext}>
                {activeConvo?.sessionId?.slice(-12)}
              </Typography>
              <Box sx={{ mt: 1 }}>
                <Chip
                  label={isLive ? "Live" : convo?.status}
                  size="small"
                  sx={{
                    bgcolor: isLive ? "#fff3e0" : convo?.status === "active" ? "#e8f5e9" : "#f0f0f0",
                    color: isLive ? ORANGE : convo?.status === "active" ? GREEN : "#888",
                    fontWeight: 700,
                  }}
                />
              </Box>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* Action Buttons */}
            <Box sx={{ mb: 2, display: "flex", flexDirection: "column", gap: 1 }}>
              <Button
                fullWidth
                variant={convo?.status !== "closed" ? "outlined" : "contained"}
                color={convo?.status !== "closed" ? "error" : "success"}
                size="small"
                startIcon={convo?.status !== "closed" ? <CheckCircleIcon /> : <ReplayIcon />}
                onClick={handleStatusToggle}
                disabled={statusUpdating}
                sx={{ textTransform: "none", borderRadius: 2 }}
              >
                {statusUpdating ? "Updating..." : convo?.status !== "closed" ? "Close Conversation" : "Reopen Conversation"}
              </Button>
              <Button
                fullWidth
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={() => exportConversation(convo, activeConvo?.userName)}
                sx={{ textTransform: "none", borderRadius: 2, borderColor: BLUE, color: BLUE, "&:hover": { bgcolor: LIGHT_BLUE, borderColor: BLUE } }}
              >
                Export Chat
              </Button>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* Stats */}
            <Typography variant="caption" color={T.subtext} fontWeight={700} sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
              Conversation Info
            </Typography>
            {[
              { label: "Bot", value: convo.botName || convo.chatbotId?.slice(-8) || "—" },
              { label: "Assigned Agent", value: convo.agentName ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: convo.agentStatus === "online" ? "#10b981" : "#6b7280", flexShrink: 0 }} />
                  <Typography variant="body2" fontWeight={600} color={T.text}>{convo.agentName}</Typography>
                </Box>
              ) : "—" },
              { label: "Total Messages", value: convo?.messages?.length || 0 },
              { label: "User Answers", value: userMessages },
              { label: "Bot Questions", value: botMessages },
              { label: "Started", value: new Date(convo.createdAt || convo.updatedAt).toLocaleDateString() },
              { label: "Last Active", value: timeAgo(convo.updatedAt) },
              { label: "Status", value: isLive ? "Live" : convo.status },
            ].map((item) => (
              <Box key={item.label} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 1, borderBottom: `1px solid ${T.border}` }}>
                <Typography variant="body2" color={T.subtext}>{item.label}</Typography>
                {typeof item.value === "object" ? item.value : <Typography variant="body2" fontWeight={600} color={T.text}>{item.value}</Typography>}
              </Box>
            ))}

            <Divider sx={{ my: 2 }} />

            {/* Recent Answers */}
            <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
              Recent Answers
            </Typography>
            {(convo?.messages || []).filter(m => m.sender === "user").slice(-4).map((m, i) => (
              <Box key={i} sx={{ mt: 1.5, p: 1.5, bgcolor: PANEL_BG, borderRadius: 2 }}>
                <Typography fontSize={12} color="text.secondary" noWrap>{m.text}</Typography>
              </Box>
            ))}
          </>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", gap: 1 }}>
            <InfoOutlinedIcon sx={{ color: "#ccc", fontSize: 40 }} />
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Select a chat to see details
            </Typography>
          </Box>
        )}
      </Box>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity={snack.severity} sx={{ borderRadius: 2 }} onClose={() => setSnack(s => ({ ...s, open: false }))}>{snack.message}</Alert>
      </Snackbar>
      {/* Transfer Dialog */}
      <Dialog open={transferOpen} onClose={() => setTransferOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
        <Box sx={{ background: "linear-gradient(135deg, #6366f1, #8b5cf6)", px: 3, py: 2.5, display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{ width: 40, height: 40, borderRadius: "12px", bgcolor: "rgba(255,255,255,0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <SwapHorizIcon sx={{ color: "#fff", fontSize: 22 }} />
          </Box>
          <Box>
            <Typography fontWeight={800} fontSize={16} color="#fff">Transfer Chat</Typography>
            <Typography fontSize={12} color="rgba(255,255,255,0.7)">Assign to another agent</Typography>
          </Box>
        </Box>
        <DialogContent sx={{ p: 3 }}>
          {agents.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 2 }}>
              <Box sx={{ width: 64, height: 64, borderRadius: "20px", bgcolor: "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 2 }}>
                <Typography fontSize={32}>👥</Typography>
              </Box>
              <Typography fontWeight={700} fontSize={15} mb={0.5}>No agents created yet</Typography>
              <Typography fontSize={13} color="text.secondary" mb={2.5}>Create agents in Team Management to enable chat transfers.</Typography>
              <Button variant="contained" onClick={() => { setTransferOpen(false); window.open("/app/team", "_blank"); }}
                sx={{ textTransform: "none", borderRadius: 2, background: "linear-gradient(135deg, #6366f1, #8b5cf6)", px: 3 }}>
                Go to Team Management
              </Button>
            </Box>
          ) : (() => {
            const onlineAgents = agents.filter(a => a.agentStatus === "online");
            const offlineAgents = agents.filter(a => a.agentStatus !== "online");
            const selObj = agents.find(a => a._id === selectedAgent);
            return (
              <>
                {onlineAgents.length === 0 && (
                  <Box sx={{ p: 1.5, bgcolor: "#fffbeb", border: "1px solid #fde68a", borderRadius: 2, mb: 2, display: "flex", gap: 1 }}>
                    <Typography fontSize={16}>⚠️</Typography>
                    <Box>
                      <Typography fontSize={13} fontWeight={700} color="#92400e">All agents are offline</Typography>
                      <Typography fontSize={12} color="#b45309">Chat will be assigned when they come online.</Typography>
                    </Box>
                  </Box>
                )}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {onlineAgents.length > 0 && <Typography fontSize={11} fontWeight={700} color="#9ca3af" sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>Online</Typography>}
                  {onlineAgents.map(a => (
                    <Box key={a._id} onClick={() => setSelectedAgent(a._id)} sx={{ p: 1.5, borderRadius: 2, cursor: "pointer", border: "2px solid", borderColor: selectedAgent === a._id ? "#6366f1" : "#f3f4f6", bgcolor: selectedAgent === a._id ? "#eef2ff" : "#fafafa", display: "flex", alignItems: "center", gap: 1.5, transition: "all 0.15s", "&:hover": { borderColor: "#6366f1", bgcolor: "#eef2ff" } }}>
                      <Avatar sx={{ width: 36, height: 36, background: "linear-gradient(135deg, #6366f1, #8b5cf6)", fontWeight: 700, fontSize: 14 }}>{a.fullName.charAt(0).toUpperCase()}</Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography fontSize={13} fontWeight={700}>{a.fullName}</Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#10b981" }} />
                          <Typography fontSize={11} color="#10b981" fontWeight={600}>{a.role} · Online</Typography>
                        </Box>
                      </Box>
                      {selectedAgent === a._id && <Box sx={{ width: 20, height: 20, borderRadius: "50%", bgcolor: "#6366f1", display: "flex", alignItems: "center", justifyContent: "center" }}><Typography fontSize={12} color="#fff">✓</Typography></Box>}
                    </Box>
                  ))}
                  {offlineAgents.length > 0 && <Typography fontSize={11} fontWeight={700} color="#9ca3af" sx={{ textTransform: "uppercase", letterSpacing: 0.5, mt: 0.5 }}>Offline</Typography>}
                  {offlineAgents.map(a => (
                    <Box key={a._id} onClick={() => setSelectedAgent(a._id)} sx={{ p: 1.5, borderRadius: 2, cursor: "pointer", border: "2px solid", borderColor: selectedAgent === a._id ? "#6b7280" : "#f3f4f6", bgcolor: "#fafafa", display: "flex", alignItems: "center", gap: 1.5, opacity: 0.7, transition: "all 0.15s", "&:hover": { opacity: 1, borderColor: "#6b7280" } }}>
                      <Avatar sx={{ width: 36, height: 36, bgcolor: "#e5e7eb", color: "#6b7280", fontWeight: 700, fontSize: 14 }}>{a.fullName.charAt(0).toUpperCase()}</Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography fontSize={13} fontWeight={700}>{a.fullName}</Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#6b7280" }} />
                          <Typography fontSize={11} color="#6b7280" fontWeight={600}>{a.role} · Offline</Typography>
                        </Box>
                      </Box>
                      {selectedAgent === a._id && <Box sx={{ width: 20, height: 20, borderRadius: "50%", bgcolor: "#6b7280", display: "flex", alignItems: "center", justifyContent: "center" }}><Typography fontSize={12} color="#fff">✓</Typography></Box>}
                    </Box>
                  ))}
                </Box>
                {selObj && selObj.agentStatus !== "online" && (
                  <Box sx={{ mt: 2, p: 1.2, bgcolor: "#fffbeb", border: "1px solid #fde68a", borderRadius: 1.5, display: "flex", gap: 0.8 }}>
                    <Typography fontSize={13}>⚠️</Typography>
                    <Typography fontSize={12} color="#92400e">{selObj.fullName} is offline. They will receive this chat when they come online.</Typography>
                  </Box>
                )}
              </>
            );
          })()}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 0, gap: 1 }}>
          <Button onClick={() => setTransferOpen(false)} fullWidth variant="outlined" sx={{ textTransform: "none", borderRadius: 2, borderColor: "#e5e7eb", color: "#6b7280" }}>Cancel</Button>
          {agents.length > 0 && (
            <Button variant="contained" onClick={handleTransfer} disabled={!selectedAgent || transferring} fullWidth
              sx={{ textTransform: "none", borderRadius: 2, background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
                "&.Mui-disabled": { background: "linear-gradient(135deg, #a5b4fc, #c4b5fd)", color: "#fff", opacity: 1 } }}>
              {transferring ? "Transferring..." : "Transfer Chat"}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
